import { Injectable } from '@angular/core';

import { CommsService, FoldersDataService } from '@services';
import { IObjectStringKeyMap } from '@shared/models';
import { Events } from '@services/events/events.service';
import { ToastService } from '@services/toast/toast.service';

import { NGXLogger } from 'ngx-logger';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep, each} from 'lodash';

export interface IGateway {
  active: number;
  createdAt: number;
  creator: number;
  disabledAt: number;
  disabledBy: number;
  lastUpdate: number;
  gatewayID: number;
  folderID: number;
  mac: string;
  name: string;
  lid: number;
  config: string;
  localconfig: string;
  bootstrap: string;
}

@Injectable({
  providedIn: 'root'
})
export class GatewayService {
  public gateways = {
    lastRequest: null,
    data: {} as IObjectStringKeyMap<IGateway>
  };

  constructor(
    private commsService: CommsService,
    private events: Events,
    private logger: NGXLogger,
    private foldersDataService: FoldersDataService,
    private toastService: ToastService,
    private translate: TranslateService
  ) {}

  public refresh(updated?: number): Promise<IObjectStringKeyMap<IGateway>> {
    if (updated && updated < this.gateways.lastRequest) {
      this.logger.log(`local gateways cache already up to date: ${updated}, ${this.gateways.lastRequest}`);
      return Promise.resolve(this.gateways.data);
    } else {
      const requestData: any = {
        cmd: 'getGateways',
        lastRequest: this.gateways.lastRequest,
      };

      return this.commsService.sendMessage(requestData, false, false).then((response) => {
        if (response?.reqStatus === 'OK') {
          this.updateCache(response);
        }

        return this.gateways.data;
      });
    }
  }

  public updateCache(data) {
    this.gateways.data = data.result.gateways;
    this.gateways.lastRequest = data.result.timestamp;
    this.events.publish('ccs:gatewaysUpdate', true);
  }

  public getLocalGateways() {
    return cloneDeep(this.gateways.data);
  }

  public getGatewaysAsList(includeInactive: boolean = false, includeDisabled: boolean = false): IGateway[] {
    const theList: IGateway[] = [];

    each(this.gateways.data, (item) => {
      if (!includeInactive && !item.active) {
        return;
      }
      if (!includeDisabled && item.disabledAt) {
        return;
      }
      theList.push(item);
    });

    return theList;
  }

  public getGateways(): IObjectStringKeyMap<IGateway> {
    return this.gateways.data;
  }

  public getGatewayById(id: number): IGateway {
    return cloneDeep(this.gateways.data[id]);
  }

  /**
   * Display gateway name with folder path.
   * Shows: folderPath/name or folderPath/mac if name is empty
   * @param gateway - Gateway object or gateway ID
   * @param includeFolders - Whether to include folder path (default: true)
   * @returns Display string for the gateway
   */
  public displayGatewayName(gateway: IGateway | number, includeFolders: boolean = true): string {
    if (typeof gateway === 'number') {
      gateway = this.getGatewayById(gateway);
    }
    if (!gateway) {
      return '';
    }
    
    // Use name if it exists, otherwise use MAC address
    const displayName = gateway.name || gateway.mac || '';
    
    // Include folder path if requested and folderID exists
    if (includeFolders && gateway.folderID) {
      const folderPath = this.foldersDataService.getFolderPath(gateway.folderID, false, '/', true);
      return `${folderPath}/${displayName}`;
    }
    
    return displayName;
  }

  public addGateway(data: any): Promise<any> {
    const requestData = {
      cmd: 'addGateway',
      ...data
    };
    return this.commsService.sendMessage(requestData, false, false);
  }

  public updateGateway(data: any): Promise<any> {
    const requestData = {
      cmd: 'updateGateway',
      ...data
    };
    return this.commsService.sendMessage(requestData, false, false);
  }

  public deleteGateway(id: number): Promise<any> {
    const requestData = {
      cmd: 'deleteGateway',
      gatewayID: id
    };

    return this.commsService.sendMessage(requestData, false, false);
  }

  public messageGateway(id: number, message: any ): Promise<any> {
    const requestData = {
      cmd: 'messageGateway',
      gatewayID: id,
      message
    };
    return this.commsService.sendMessage(requestData, false, false);
  }

  // The server answers OK once the message is published to the gateway; it does not wait for the gateway to act.
  public async messageGatewayWithToast(id: number, message: any, name: string): Promise<void> {
    try {
      const response = await this.messageGateway(id, message);
      if (response?.reqStatus === 'OK') {
        this.toastService.show(this.translate.instant('SHARED.Gateway_Message_Sent', { name }));
        return;
      }
    } catch (err) {
      this.logger.error('messageGateway failed', err);
    }
    this.toastService.showError(this.translate.instant('SHARED.Gateway_Message_Failed', { name }));
  }

}
